/**
 * Situational team shape — the block re-shapes for the STATE OF THE GAME. (§8.2, §10.1 Layer 3)
 *
 * Owner directive: *"payers formations to situations"* — the team shape must
 * change with the game the way a real side's does, not sit in one static 4-4-2
 * for ninety minutes.
 *
 * ── WHAT WAS THERE BEFORE, AND WHY IT WASN'T ENOUGH ─────────────────────────
 *
 * team-ai.ts's solveFormation already moved the block's HEIGHT: a phase FSM
 * (BuildUp / Attack / FinalThird / Transition / Press / DefendBlock) selected one
 * of six AI_PHASE_SHIFT_* numbers, and the whole block slid ballward linearly.
 * Three things were missing, and all three are visible from the stands:
 *
 *   1. The block's LENGTH never changed. Out of possession the strikers stayed
 *      37 m off the midfield band (MEASURED: gapMidFwd 33.5 m, teamLength 64 m —
 *      see the before/after table below). That is a side, not a block.
 *   2. The block's WIDTH never changed at all. Nothing in the solver touched fz
 *      except the anti-stacking spread, so a pressing team was exactly as wide as
 *      an attacking one.
 *   3. NOTHING read the scoreboard or the clock. A team 3-0 down with two minutes
 *      left stood in the shape it used at 0-0 in minute one.
 *
 * This module is the missing half, expressed as five NAMED RULES rather than
 * scattered arithmetic. Each rule reads as "this is what the team does when X".
 *
 * ── THE FIVE RULES ──────────────────────────────────────────────────────────
 *
 *   R1  COMPRESS OUT OF POSSESSION      forwards come back, the side narrows
 *   R2  SPREAD AND PUSH IN POSSESSION   forwards stretch them, wide roles go wide
 *   R3  THE BLOCK SLIDES BY THIRD       low / mid / high block — three heights,
 *                                       not two halves
 *   R4  COUNTER-ATTACK / COUNTER-PRESS  the 0.75 s after the ball changes hands
 *   R5  SCORELINE + TIME REMAINING      chase the game / protect the lead
 *
 * Rules compose additively on depth (dFx) and multiplicatively on width
 * (widthScale). That ordering is deliberate: two rules that both want the block
 * deeper should stack, but two rules that both want it narrow must not collapse
 * it into a huddle — a product of sub-1 factors converges on zero far more slowly
 * than a sum of subtractions would.
 *
 * ── DETERMINISM ─────────────────────────────────────────────────────────────
 *
 * Pure functions of (WorldState, team, attackSign, two phase booleans). No RNG
 * draw of any kind — not world.rng, not the AI's own keyed stream — so this
 * cannot perturb the sim's stream (§15.3, DECISIONS.md D5) and adds nothing to
 * the determinism contract. No trig, so no dsin/dcos wrappers are needed and
 * the /ai lint bans are satisfied trivially. No wall clock: "time remaining" is
 * match.clockTicks, which IS sim state.
 *
 * ── WHERE THE NUMBERS LIVE ──────────────────────────────────────────────────
 *
 * Every tunable is a SHAPE_* constant in sim/constants.ts with a WHY comment
 * (§15.5). Nothing numeric is typed in this file except the band predicates'
 * structural constants.
 */

import type { Position, WorldState } from '../sim/types.js';
import {
  AI_BUILD_UP_FX,
  AI_FINAL_THIRD_FX,
  SHAPE_BUILD_UP_DEF_DROP,
  SHAPE_BUILD_UP_FWD_OUTLET,
  SHAPE_CHASE_FWD_EXTRA,
  SHAPE_CHASE_PUSH,
  SHAPE_CHASE_SPREAD,
  SHAPE_COUNTERPRESS_MID_PUSH,
  SHAPE_COUNTERPRESS_NARROW,
  SHAPE_COUNTER_FWD_SURGE,
  SHAPE_COUNTER_MID_SURGE,
  SHAPE_COUNTER_SPREAD,
  SHAPE_FINAL_THIRD_FWD_PUSH,
  SHAPE_FINAL_THIRD_SPREAD,
  SHAPE_GOAL_SWING_FULL,
  SHAPE_HIGH_BLOCK_PUSH,
  SHAPE_LATE_GAME_REMAINING_FRAC,
  SHAPE_LOW_BLOCK_DROP,
  SHAPE_LOW_BLOCK_NARROW,
  SHAPE_MID_BLOCK_DEF_HOLD,
  SHAPE_OOP_FORWARD_DROP,
  SHAPE_OOP_MID_DROP,
  SHAPE_OOP_NARROW,
  SHAPE_POSS_FORWARD_PUSH,
  SHAPE_POSS_MID_PUSH,
  SHAPE_POSS_SPREAD,
  SHAPE_PROTECT_DROP,
  SHAPE_PROTECT_NARROW,
  SIMULATION_TICK_RATE,
} from '../sim/constants.js';
import { isDefenderPosition, isForwardPosition } from './formations.js';
import { clamp01 } from '../math/vec3.js';

// ---------------------------------------------------------------------------
// Bands — the three lines of a football team
// ---------------------------------------------------------------------------

/**
 * The three lines every formation has, whatever its name. Identical partition to
 * the one tactical-metrics.ts's gapDefMid / gapMidFwd are measured over
 * (measureLineSpacing) — deliberately, so a rule written here moves the metric
 * that reports it, and a regression cannot hide in a band-definition mismatch.
 */
export const enum ShapeBand {
  Def = 0,
  Mid = 1,
  Fwd = 2,
}

/** DEF = isDefenderPosition (GK included; the GK's shape is never solved
 *  anyway — team-ai's ownsSlot skips him), FWD = isForwardPosition, MID = rest. */
export function shapeBandOf(pos: Position): ShapeBand {
  if (isForwardPosition(pos)) return ShapeBand.Fwd;
  if (isDefenderPosition(pos)) return ShapeBand.Def;
  return ShapeBand.Mid;
}

// ---------------------------------------------------------------------------
// The situation — everything about the game state the shape reacts to
// ---------------------------------------------------------------------------

/**
 * The state of the game, from ONE team's point of view, reduced to exactly the
 * facts the five rules read. Filled by describeShapeSituation() into a
 * caller-owned object so the 5Hz solver allocates nothing (§15.7).
 */
export interface ShapeSituation {
  /** We have the ball (the team FSM's BuildUp / Attack / FinalThird). */
  inPossession: boolean;
  /** Transition window AND we are the side that just WON it. */
  counterAttack: boolean;
  /** Transition window AND we are the side that just LOST it. */
  counterPress: boolean;
  /** Which third the ball is in ALONG OUR OWN ATTACK AXIS: −1 = our defensive
   *  third, 0 = middle third, +1 = our attacking third. Attack-relative, so it
   *  survives the half-time switch of ends without special-casing. */
  ballThird: -1 | 0 | 1;
  /** Ball is in OUR half. Carried separately from ballThird because the team
   *  FSM splits defending at the HALFWAY line, not at a third — so the middle
   *  third straddles Press and DefendBlock, and R3's mid-block hold must only
   *  apply on the side of it where the block is actually dropping. */
  ballOwnHalf: boolean;
  /** 0..1 urgency to chase the game — losing, second half, late. */
  chase: number;
  /** 0..1 urgency to protect a lead — winning, second half, late. */
  protect: number;
}

export function createShapeSituation(): ShapeSituation {
  return {
    inPossession: false,
    counterAttack: false,
    counterPress: false,
    ballThird: 0,
    ballOwnHalf: false,
    chase: 0,
    protect: 0,
  };
}

/** Whose ball it effectively is: the carrier's team, else who touched last.
 *  Same carrier-else-last-touch convention as team-ai.ts's effectiveBallTeam
 *  and tactical-metrics.ts's possessorTeam — one definition of "possession"
 *  across the AI, the instrument and this module. */
function effectiveBallTeam(world: WorldState): number {
  const owner = world.ball.possessedBy;
  if (owner >= 0) return world.players.team[owner]!;
  return world.ball.lastTouchedTeam;
}

/**
 * How much of the current half is LEFT, as a fraction ∈ [0, 1] (added time
 * included). Mirrors rules/fsm.ts:isLosingLateForExtraAttacker exactly — same
 * formula, same added-time handling — so "late in the game" means one thing in
 * the corner choreography and in the team's shape. Returns 1 (i.e. "not late")
 * when the half length is degenerate, so a zero-length test world never reads
 * as the dying seconds.
 */
function remainingFracOfHalf(world: WorldState): number {
  const m = world.match;
  const halfTicks = Math.round(world.config.halfLengthS * SIMULATION_TICK_RATE) + m.addedTimeTicks;
  if (halfTicks <= 0) return 1;
  return clamp01(1 - m.clockTicks / halfTicks);
}

/**
 * R5's two urgencies, derived together because they are the two signs of one
 * quantity and must never both be non-zero.
 *
 * GATE (the CORNER_LATE_GAME_REMAINING_FRAC precedent, rules/fsm.ts:1228):
 * second half only, and only inside the last SHAPE_LATE_GAME_REMAINING_FRAC of
 * it. Level ⇒ neither urgency: a drawn game has nothing to chase or protect.
 *
 * RAMP, not a switch: lateness goes 0 → 1 across the window, so a chasing side
 * drifts forward over the closing minutes instead of snapping into a new
 * formation on one tick. Multiplied by the goal swing, saturating at
 * SHAPE_GOAL_SWING_FULL.
 */
function urgencies(world: WorldState, team: number, out: ShapeSituation): void {
  out.chase = 0;
  out.protect = 0;
  const m = world.match;
  if (m.half < 2) return;
  const remaining = remainingFracOfHalf(world);
  if (remaining > SHAPE_LATE_GAME_REMAINING_FRAC) return;
  const lateness = clamp01((SHAPE_LATE_GAME_REMAINING_FRAC - remaining) / SHAPE_LATE_GAME_REMAINING_FRAC);
  const myScore = team === 0 ? m.scoreA : m.scoreB;
  const oppScore = team === 0 ? m.scoreB : m.scoreA;
  const deficit = oppScore - myScore;
  if (deficit > 0) out.chase = lateness * clamp01(deficit / SHAPE_GOAL_SWING_FULL);
  else if (deficit < 0) out.protect = lateness * clamp01(-deficit / SHAPE_GOAL_SWING_FULL);
}

/**
 * Read the game state into `out`. READ-ONLY over `world`.
 *
 * `inPossession` and `transition` come from the caller because they are the team
 * AI's own phase FSM output (team-ai.ts determinePhase) and re-deriving them here
 * would be a second, drifting copy of the FSM. Which SIDE of the transition we
 * are on is derived here, from the ball, because the FSM does not record it: both
 * teams are in Transition at the same moment and only the ball says who won it.
 */
export function describeShapeSituation(
  world: WorldState,
  team: number,
  attackSign: number,
  inPossession: boolean,
  transition: boolean,
  out: ShapeSituation,
): void {
  out.inPossession = inPossession;

  const ballTeam = effectiveBallTeam(world);
  out.counterAttack = transition && ballTeam === team;
  out.counterPress = transition && ballTeam >= 0 && ballTeam !== team;

  const halfL = world.config.pitchLength * 0.5;
  const ballFx = halfL > 0 ? (attackSign * world.ball.px) / halfL : 0;
  out.ballThird = ballFx > AI_FINAL_THIRD_FX ? 1 : ballFx < AI_BUILD_UP_FX ? -1 : 0;
  out.ballOwnHalf = ballFx < 0;

  urgencies(world, team, out);
}

// ---------------------------------------------------------------------------
// The adjustment the solver applies
// ---------------------------------------------------------------------------

/** What the five rules produce for one band. Caller-owned; we only fill. */
export interface ShapeAdjust {
  /** Additive shift along the team's own attack axis, in POSITION_ANCHORS'
   *  normalized fx units (1.0 = a half-pitch). Positive = further forward. */
  dFx: number;
  /** Multiplier on the anchor's |fz| — its distance off the centre line.
   *  < 1 narrows the block, > 1 stretches it toward the touchlines. The
   *  anti-stacking spread (AI_SHAPE_SPREAD_FZ) is NOT scaled by this: it exists
   *  to stop two CBs standing on each other, and a narrow block still must not. */
  widthScale: number;
}

export function createShapeAdjust(): ShapeAdjust {
  return { dFx: 0, widthScale: 1 };
}

/**
 * The five rules, for one band of one team in one situation. Pure; `out` is
 * fully overwritten, never accumulated across calls.
 *
 * Read it top to bottom as a coach's instructions.
 */
export function situationalShape(sit: ShapeSituation, band: ShapeBand, out: ShapeAdjust): void {
  let dFx = 0;
  let width = 1;

  // ── R1  COMPRESS OUT OF POSSESSION ──────────────────────────────────────
  // "When they have it: strikers, come back to the midfield line. Everyone,
  //  tuck in off the touchline." The forwards come back ~3x as far as the
  //  midfield does — halfway back, not all the way (AI_FORWARD_DROP_DAMP's
  //  reasoning: a striker who defends his own box has nobody to pass to on the
  //  turnover), and not the 37 m hole the base anchors left.
  if (!sit.inPossession) {
    if (band === ShapeBand.Fwd) dFx -= SHAPE_OOP_FORWARD_DROP;
    else if (band === ShapeBand.Mid) dFx -= SHAPE_OOP_MID_DROP;
    width *= SHAPE_OOP_NARROW;
  }

  // ── R2  SPREAD AND PUSH IN POSSESSION ───────────────────────────────────
  // "When we have it: strikers, stretch their back line. Midfield, follow so we
  //  stay connected. Wide men, touchline — make the pitch big."
  if (sit.inPossession) {
    if (band === ShapeBand.Fwd) dFx += SHAPE_POSS_FORWARD_PUSH;
    else if (band === ShapeBand.Mid) dFx += SHAPE_POSS_MID_PUSH;
    width *= SHAPE_POSS_SPREAD;
  }

  // ── R3  THE BLOCK SLIDES WITH THE BALL, BY THIRD ────────────────────────
  // Three defensive heights instead of two halves. The MID-block hold is the
  // load-bearing one: without it AI_PHASE_SHIFT_DEFEND_BLOCK × the defender gain
  // put the back line on its own goal line for a ball in the CENTRE CIRCLE.
  if (!sit.inPossession) {
    if (sit.ballThird < 0) {
      // Our defensive third — low block. Settle and squeeze the box.
      dFx -= SHAPE_LOW_BLOCK_DROP;
      width *= SHAPE_LOW_BLOCK_NARROW;
    } else if (sit.ballThird === 0) {
      // Middle third — MID block. Hold the back line up; do not lay siege.
      // Own-half side only: above halfway the FSM is already in Press and the
      // line is being PUSHED, so holding it further would put the midfield band
      // behind the back four (measured, see SHAPE_MID_BLOCK_DEF_HOLD).
      if (band === ShapeBand.Def && sit.ballOwnHalf) dFx += SHAPE_MID_BLOCK_DEF_HOLD;
    } else {
      // Their third — high block. The whole team steps up behind the press.
      dFx += SHAPE_HIGH_BLOCK_PUSH;
    }
  } else {
    if (sit.ballThird > 0) {
      // Our attacking third — attack the box and make the pitch as wide as it
      // will go (crosses need a far post to aim at).
      if (band === ShapeBand.Fwd) dFx += SHAPE_FINAL_THIRD_FWD_PUSH;
      width *= SHAPE_FINAL_THIRD_SPREAD;
    } else if (sit.ballThird < 0) {
      // Our own third with the ball — build-up, and the ONE situation where a
      // LONG team is correct. The back line splits deeper to give the keeper and
      // each other an angle; the forwards stay high on the last defender,
      // because the space is behind a high press and a striker who drops in to
      // help removes the only reason pressing us is risky. This is also what
      // carries a counter past R4's 0.75 s window (SHAPE_BUILD_UP_FWD_OUTLET).
      if (band === ShapeBand.Def) dFx -= SHAPE_BUILD_UP_DEF_DROP;
      else if (band === ShapeBand.Fwd) dFx += SHAPE_BUILD_UP_FWD_OUTLET;
    }
  }

  // ── R4  COUNTER-ATTACK / COUNTER-PRESS ──────────────────────────────────
  // The 0.75 s after the ball changes hands (AI_TRANSITION_TICKS). Won it:
  // GO — the one moment in football where being out of shape is correct, so this
  // is the largest single dFx in the module. Lost it: do NOT drop off, squeeze.
  if (sit.counterAttack) {
    if (band === ShapeBand.Fwd) dFx += SHAPE_COUNTER_FWD_SURGE;
    else if (band === ShapeBand.Mid) dFx += SHAPE_COUNTER_MID_SURGE;
    width *= SHAPE_COUNTER_SPREAD;
  } else if (sit.counterPress) {
    if (band === ShapeBand.Mid) dFx += SHAPE_COUNTERPRESS_MID_PUSH;
    width *= SHAPE_COUNTERPRESS_NARROW;
  }

  // ── R5  SCORELINE + TIME REMAINING ──────────────────────────────────────
  // Losing late: everyone up, forwards further still, and go wide for crosses.
  // Protecting a lead late: drop off and narrow — defend the middle and make
  // them play round you. Never both (urgencies() guarantees it).
  if (sit.chase > 0) {
    dFx += sit.chase * SHAPE_CHASE_PUSH;
    if (band === ShapeBand.Fwd) dFx += sit.chase * SHAPE_CHASE_FWD_EXTRA;
    width *= 1 + sit.chase * (SHAPE_CHASE_SPREAD - 1);
  } else if (sit.protect > 0) {
    dFx -= sit.protect * SHAPE_PROTECT_DROP;
    width *= 1 - sit.protect * (1 - SHAPE_PROTECT_NARROW);
  }

  out.dFx = dFx;
  out.widthScale = width;
}
